import { hasAny, nonEmptyBullets, fmtRange, getSectionMarkerAttrs } from "./utils";
import { ContactRow } from "./ContactBits";

export default function MinimalTemplate({ resume }) {
  const { personal, summary, experience, education, skills, projects, certifications, languages } = resume;
  return (
    <div
      style={{
        fontFamily: "'IBM Plex Mono', monospace",
        color: "#0a0a0a",
        background: "#ffffff",
        padding: "56px 64px",
        fontSize: 9.5,
        lineHeight: 1.55,
        width: "100%",
        height: "100%",
        boxSizing: "border-box",
      }}
    >
      <header style={{ marginBottom: 22 }}>
        <div data-resume-h1 style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 700, fontSize: 22, letterSpacing: "-0.01em" }}>
          {personal.fullName || "Your Name"}
        </div>
        {personal.title && (
          <div style={{ fontSize: 9.5, color: "#525252", marginTop: 2 }}>
            {personal.title}
          </div>
        )}
        <ContactRow
          personal={personal}
          size={9}
          color="#525252"
          style={{ marginTop: 10, fontSize: 9, gap: 8 }}
        />
      </header>

      {summary && (
        <Section label="Professional Summary">
          <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{summary}</div>
        </Section>
      )}

      {skills?.length > 0 && (
        <Section label="SKILLS">
          <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{skills.join(", ")}</div>
        </Section>
      )}

      {hasAny(experience, ["company", "role"]) && (
        <Section label="WORK">
          {experience.map((e, i) => {
            const bullets = nonEmptyBullets(e.bullets);
            if (!e.company && !e.role && bullets.length === 0) return null;
            return (
              <Row key={e.id || i} meta={<span data-resume-date>{fmtRange(e.startDate, e.endDate)}</span>}>
                <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 600, fontSize: 10.5 }}>
                  <span data-resume-jobtitle>{e.role}</span> <span data-resume-company style={{ fontWeight: 400, color: "#525252" }}>— {e.company}</span>
                </div>
                {e.location && <div style={{ color: "#737373" }}><span data-resume-location>{e.location}</span></div>}
                {bullets.length > 0 && (
                  <ul data-resume-workdesc style={{ margin: "4px 0 0 0", padding: "0 0 0 14px", fontFamily: "'IBM Plex Sans', sans-serif", fontSize: 10 }}>
                    {bullets.map((b, bi) => (
                      <li key={`${e.id || i}-b-${bi}`} style={{ marginBottom: 2 }}>{b}</li>
                    ))}
                  </ul>
                )}
              </Row>
            );
          })}
        </Section>
      )}

      {hasAny(education, ["school", "degree"]) && (
        <Section label="EDUCATION">
          {education.map((ed, i) => (ed.school || ed.degree) && (
            <Row key={ed.id || i} meta={fmtRange(ed.startDate, ed.endDate)}>
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 600, fontSize: 10.5 }}>
                {ed.school} <span style={{ fontWeight: 400, color: "#525252" }}>— {ed.degree}{ed.field ? `, ${ed.field}` : ""}</span>
              </div>
              {ed.details && <div style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "#525252" }}>{ed.details}</div>}
            </Row>
          ))}
        </Section>
      )}

      {hasAny(projects, ["name"]) && (
        <Section label="PROJECTS">
          {projects.map((p, i) => p.name && (
            <Row key={p.id || i} meta={p.tech}>
              <div data-resume-projectname style={{ fontFamily: "'IBM Plex Sans', sans-serif", fontWeight: 700, fontSize: 10.5 }}>{p.name}</div>
              {p.description && <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{p.description}</div>}
            </Row>
          ))}
        </Section>
      )}

      {hasAny(certifications, ["name"]) && (
        <Section label="CERTIFICATIONS">
          {certifications.map((c, i) => c.name && (
            <Row key={c.id || i} meta={c.date}>
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{c.name} <span style={{ color: "#525252" }}>— {c.issuer}</span></div>
            </Row>
          ))}
        </Section>
      )}

      {languages?.some((l) => l.name?.trim()) && (
        <Section label="LANGUAGES">
          {languages.map((l) => l.name?.trim() && (
            <Row key={l.id} meta={l.level}>
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>{l.name}</div>
            </Row>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ label, children }) {
  // Fix iter 45: was a 80px | 1fr grid — with Section Headers now scaling
  // up to 18 pt (per user spec) the label routinely overflowed 80 px and
  // visually overlapped the content column (Certifications overlap bug).
  // Stack label above content in a single column so nothing can overlap
  // regardless of header font-size.
  return (
    <section {...getSectionMarkerAttrs(label)} style={{ marginBottom: 16 }}>
      <div data-resume-h2 style={{ fontSize: 8, fontWeight: 700, letterSpacing: "0.2em", color: "#a3a3a3", marginBottom: 6, textTransform: "uppercase" }}>{label}</div>
      <div>{children}</div>
    </section>
  );
}

function Row({ meta, children }) {
  return (
    <div data-page-item="true" style={{ marginBottom: 10 }}>
      {meta && <div style={{ fontSize: 8.5, color: "#a3a3a3", letterSpacing: "0.1em" }}>{meta}</div>}
      {children}
    </div>
  );
}
